import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import SubscriptionStatusCard from "@/components/SubscriptionStatusCard";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Sparkles } from "lucide-react";

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.plans || []));
    api.get("/subscription/status").then((r) => setStatus(r.data));
  }, []);

  const locked = status && (status.state === "ACTIVE" || status.state === "PAYMENT_PENDING");

  const choose = (planId) => {
    navigate(`/payment/${planId}`);
  };

  return (
    <div className="min-h-screen bg-slate-50 grain-bg">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center">
          <h1 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Choose your plan</h1>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">
            Pay instantly via UPI QR, upload the screenshot and UTR, and get activated once admin verifies. No cards, no account logins needed.
          </p>
        </div>

        <div className="mt-6">
          <SubscriptionStatusCard status={status} />
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-6" data-testid="plans-grid">
          {plans.map((p) => {
            const isPro = p.plan_id === "pro";
            const isAdvanced = p.plan_id === "advanced";
            return (
              <div
                key={p.plan_id}
                data-testid={`plan-card-${p.plan_id}`}
                className={`relative rounded-2xl p-6 border-2 bg-white ${isAdvanced ? "border-teal-500 shadow-xl" : "border-slate-200"}`}
              >
                {isAdvanced && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                    POPULAR
                  </span>
                )}
                {isPro && (
                  <span className="absolute top-4 right-4 bg-amber-100 border border-amber-300 text-amber-800 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">
                    Verified
                  </span>
                )}
                <h3 className="font-heading text-xl font-bold uppercase tracking-wide">{p.name}</h3>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-extrabold text-slate-900">₹{p.price}</span>
                  <span className="text-sm text-slate-500">/ {p.billing_cycle}</span>
                </div>
                <p className="text-sm text-slate-600 mt-2">{p.description}</p>
                <ul className="mt-5 space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-teal-600 mt-0.5" /> {p.duration_days} days access</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-teal-600 mt-0.5" /> Full pincode directory</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 text-teal-600 mt-0.5" /> Unlimited 1-on-1 chat</li>
                  {isPro && <li className="flex items-start gap-2"><Check className="w-4 h-4 text-amber-600 mt-0.5" /> Verified badge on your profile</li>}
                </ul>
                <Button
                  data-testid={`choose-plan-${p.plan_id}`}
                  disabled={locked}
                  onClick={() => choose(p.plan_id)}
                  className={`mt-6 w-full rounded-full ${isAdvanced ? "bg-teal-600 hover:bg-teal-700" : "bg-slate-900 hover:bg-slate-800"} text-white disabled:opacity-50`}
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Choose {p.name}
                </Button>
              </div>
            );
          })}
        </div>

        {locked && (
          <p className="mt-6 text-center text-sm text-slate-500">
            {status.state === "ACTIVE" ? "You already have an active subscription." : "A payment is pending verification — please wait for admin approval."}
          </p>
        )}
      </main>
    </div>
  );
}
